import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FileRef } from "@studio-features/meta-crew/library-adapter";
import type { Guideline, RefineAction, RefineAttempt, RefineJob, RefineStatus, ReviewNotice } from "@studio-features/meta-crew/refine-data";
import { api, errorMessage } from "./api";
import { fmtDateTime } from "./format";
import { LIVE } from "./queries";
import type { Me } from "./types";
import { can } from "./access";

/**
 * 원고 정형화·검수 API (docs/meta-crew-manuscript-refine-design.md 9절, 요청서 29절) → 시안 refine-data.ts 의 화면 모델.
 * 서버 DTO(attemptList·latestAttempt·output.report …)를 여기서 RefineJob/RefineAttempt 로 바꾼다. 판정·검토·보고서·비용은 서버 값 그대로,
 * 화면에서 다시 계산하지 않는다. 이전 시도는 각각 `GET …/attempts/:seq` 로 읽어 넣는다 (최신 결과를 복제하지 않음).
 */

type NoticeSummary = { total: number; warn: number };
type ServerFile = { fileId: string; name: string; finalName: string; size: number; kind: string; width: number | null; height: number | null };
type ServerReport = {
  status: string;
  hold: { reason: string }[];
  formatConflicts: { line: number | null; text: string; effect: string }[];
  removedInstructions: { text: string; movedTo: string }[];
  removedMeta: { text: string; movedTo: string }[];
  unusedFiles: string[];
  repeatedSentences: { text: string; count: number }[];
  scale: { media: number; linkCards: number; headings: number; paragraphs: number; videos: number; warnings: string[] };
  notes: string[];
};
type ServerRegistration = { targetCustomer: string | null; seoKeywords: string[]; schedule: { date: string | null; time: string | null; note: string | null } | null; memo: string | null };
type ServerContext = { staffNote?: string | null; priorRejection?: string | null; priorHold?: string[]; priorNotices?: string[]; priorConflicts?: string[] };

/** 시도 요약 (목록·이력). 본문 없음 */
export type AttemptSummary = {
  seq: number;
  status: "queued" | "running" | "done" | "failed" | "cancelled";
  sourceRevision: number;
  guidelineId: string;
  guidelineVersion: number;
  guidelineLabel: string;
  provider: "openai" | "codex";
  model: string;
  effort: string | null;
  reportStatus: string | null;
  hold: number;
  errors: number;
  warnings: number;
  notices: NoticeSummary;
  costUsd: number | null;
  error: string | null;
  decision: "registered" | "rejected" | null;
  decisionNote: string | null;
  /** 이 시도로 등록된 원고 버전 (요청서 32절, 이전 등록은 없음) */
  registeredRevision?: number | null;
  decidedAt: string | null;
  decidedByName: string | null;
  hasContext: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
};
/** 시도 전체 (상세) */
export type AttemptDetail = AttemptSummary & {
  context: ServerContext;
  output: { manuscript: string; registration: ServerRegistration; report: ServerReport } | null;
  outputBody: string | null;
  outputFiles: { fileId: string; name: string }[];
  parse: { errors: string[]; warnings: string[]; title: string; mediaCount: number; blockCounts: Record<string, number> } | null;
  noticeList: ReviewNotice[];
  usage: Record<string, number> | null;
  /** 설계 14-3: 이 시도가 쓴 원본 버전의 보조 문서 요약·지원하지 않는 파일 (옛 서버 응답에는 없음 → 화면은 "—") */
  aux?: { name: string; chars: number; truncated: boolean }[];
  unsupportedFiles?: string[];
};
export type RefineRow = {
  id: string;
  customerId: string;
  customerName: string;
  folderId: string | null;
  title: string;
  status: RefineStatus;
  sourceRevision: number;
  files: number;
  videos: number;
  attempts: number;
  latest: AttemptSummary | null;
  manuscriptId: string | null;
  createdAt: string;
  updatedAt: string;
  createdByName: string | null;
};
export type RefineDetailDto = RefineRow & {
  sourceBody: string;
  sourceFiles: ServerFile[];
  instructions: string | null;
  sources: { revision: number; note: string | null; files: number; createdAt: string; createdByName: string | null }[];
  attemptList: AttemptSummary[];
  latestAttempt: AttemptDetail | null;
  manuscript: { id: string; title: string; status: string; revision: number } | null;
  canRegister: boolean;
  canRerun: boolean;
  /** 등록할 시도 = 가장 최근 완료 (서버가 고름) */
  registrationAttemptSeq?: number | null;
};
type SourceDto = { revision: number; body: string; files: ServerFile[]; note: string | null; createdAt: string };
export type GuidelineRow = {
  id: string;
  kind: "refine" | "generate";
  version: number;
  label: string;
  status: "active" | "inactive" | "retired";
  note: string | null;
  createdAt: string;
  createdByName: string | null;
  attempts: number;
  body?: string;
};

export const refineKeys = {
  list: (q: RefineListQuery) => ["admin", "refine", "list", q] as const,
  detail: (id: string) => ["admin", "refine", "detail", id] as const,
  attempt: (id: string, seq: number) => ["admin", "refine", "attempt", id, seq] as const,
  source: (id: string, revision: number) => ["admin", "refine", "source", id, revision] as const,
  guidelines: ["admin", "manuscript-guidelines"] as const,
  guideline: (id: string) => ["admin", "manuscript-guidelines", id] as const,
};

export type RefineListQuery = { q: string; customerId: string; status: string; offset: number; limit: number; /** 방금 만든 작업만 (쉼표) */ ids?: string; /** 대상 폴더 (요청서 35-1, "none" = 폴더 밖) */ folderId?: string };

const qs = (o: Record<string, string | number | undefined>) =>
  "?" +
  Object.entries(o)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join("&");

const fileRefs = (files: ServerFile[] | undefined): FileRef[] => (files ?? []).map((f) => ({ fileId: f.fileId, name: f.finalName ?? f.name }));
const decisionLabel = (d: AttemptSummary["decision"]) => (d === "registered" ? "등록됨" : d === "rejected" ? "반려" : undefined);
const durationOf = (a: AttemptSummary) => (a.startedAt && a.finishedAt ? Math.max(0, Date.parse(a.finishedAt) - Date.parse(a.startedAt)) : 0);
const emptyReport = (): RefineAttempt["report"] => ({
  status: "",
  hold: [],
  conflicts: [],
  removed: [],
  unusedFiles: [],
  repeated: [],
  scale: { media: 0, videos: 0, linkCards: 0, headings: 0, paragraphs: 0 },
  registration: { keywords: [], schedule: "", memo: "" },
});

/** 재검수 컨텍스트 → 사람이 읽는 줄 (어디서 온 사실인지 앞에 적는다). */
export function contextLines(c: ServerContext | null | undefined): string[] {
  if (!c) return [];
  const out: string[] = [];
  if (c.staffNote) out.push(`직원 메모: ${c.staffNote}`);
  if (c.priorRejection) out.push(`이전 반려 사유: ${c.priorRejection}`);
  for (const h of c.priorHold ?? []) out.push(`이전 보류: ${h}`);
  for (const n of c.priorNotices ?? []) out.push(`이전 자동 검토: ${n}`);
  for (const x of c.priorConflicts ?? []) out.push(`이전 형식 충돌: ${x}`);
  return out;
}

/** 시도 전체 → 시안 RefineAttempt. 보고서 항목 이름을 시안에 맞게 바꾼다 (hold[].reason → 문자열, formatConflicts → conflicts …). 예상 단계 수는 서버가 주지 않으므로 뺀다. */
export function toAttempt(a: AttemptDetail): RefineAttempt {
  const r = a.output?.report;
  const reg = a.output?.registration;
  const schedule = reg?.schedule ? [reg.schedule.date, reg.schedule.time, reg.schedule.note].filter(Boolean).join(" ") : "";
  return {
    seq: a.seq,
    sourceRevision: a.sourceRevision,
    guideline: { id: a.guidelineId, version: a.guidelineVersion, label: a.guidelineLabel },
    model: a.model,
    provider: a.provider === "codex" ? "codex" : "openai",
    status: a.status,
    costUsd: a.costUsd ?? 0,
    durationMs: durationOf(a),
    startedAt: a.startedAt ?? undefined,
    decision: decisionLabel(a.decision),
    decisionNote: a.decisionNote ?? undefined,
    registeredRevision: a.registeredRevision ?? undefined,
    error: a.error ?? undefined,
    body: a.outputBody ?? "",
    report: {
      ...(r
        ? {
          status: r.status,
          hold: r.hold.map((h) => h.reason),
          conflicts: r.formatConflicts.map((c) => ({ line: c.line ?? 0, text: c.text, effect: c.effect })),
          removed: [...r.removedInstructions, ...r.removedMeta].map((x) => ({ text: x.text, movedTo: x.movedTo })),
          unusedFiles: r.unusedFiles,
          repeated: r.repeatedSentences,
          // 예상 입력 단계 = 파서 블록 수 (브리지는 블록 하나를 한 단계로 입력한다, docs/meta-crew-manuscript-format.md)
          scale: { media: r.scale.media, videos: r.scale.videos, linkCards: r.scale.linkCards, headings: r.scale.headings, paragraphs: r.scale.paragraphs, steps: stepsOf(a.parse?.blockCounts) },
          registration: { keywords: reg?.seoKeywords ?? [], schedule, memo: reg?.memo ?? "" },
        }
        : emptyReport()),
      // 설계 14-3: 보고서 옆 "지원하지 않는 파일 n"·"보조 문서 n" — 서버가 안 주면 undefined 그대로 (0 으로 단정하지 않음)
      unsupportedFiles: a.unsupportedFiles,
      auxDocuments: a.aux,
    },
    notices: a.noticeList ?? [],
    parse: { errors: a.parse?.errors ?? [], warnings: a.parse?.warnings ?? [] },
    context: contextLines(a.context),
  };
}

function stepsOf(blockCounts?: Record<string, number> | null): number | null {
  if (!blockCounts) return null;
  const n = Object.values(blockCounts).reduce((s, v) => s + (Number(v) || 0), 0);
  return n > 0 ? n : null;
}

/**
 * 목록 행의 최신 시도 요약 → 수만 맞춘 RefineAttempt (목록은 보류·검토·오류 "개수"와 비용만 보인다).
 * 본문·보고서 내용은 없고, 상세를 열면 실제 시도로 바뀐다.
 */
function countedAttempt(a: AttemptSummary | null): RefineAttempt {
  if (!a) return { ...toAttempt({ seq: 0, status: "queued", sourceRevision: 1, guidelineId: "", guidelineVersion: 0, guidelineLabel: "", provider: "openai", model: "", effort: null, reportStatus: null, hold: 0, errors: 0, warnings: 0, notices: { total: 0, warn: 0 }, costUsd: null, error: null, decision: null, decisionNote: null, decidedAt: null, decidedByName: null, hasContext: false, startedAt: null, finishedAt: null, createdAt: "", context: {}, output: null, outputBody: null, outputFiles: [], parse: null, noticeList: [], usage: null }) };
  const base = toAttempt({ ...a, context: {}, output: null, outputBody: null, outputFiles: [], parse: null, noticeList: [], usage: null });
  return {
    ...base,
    report: { ...base.report, status: a.reportStatus ?? "", hold: Array.from({ length: a.hold }, () => "") },
    notices: Array.from({ length: a.notices?.total ?? 0 }, () => ({ code: "", level: "info" as const, message: "" })),
    parse: { errors: Array.from({ length: a.errors }, () => ""), warnings: Array.from({ length: a.warnings }, () => "") },
  };
}

/** 서버 상태·권한 → 할 수 있는 동작. 서버도 같은 규칙으로 막는다 (8절). */
export function allowedActionsOf(r: RefineRow & Partial<Pick<RefineDetailDto, "canRegister" | "canRerun">>, me: Me): RefineAction[] {
  const refine = can(me, "MANUSCRIPT_REFINE");
  const write = can(me, "MANUSCRIPT_WRITE");
  const busy = r.status === "queued" || r.status === "running";
  const out: RefineAction[] = [];
  if (write && (r.canRegister ?? (r.status === "review" && !!r.latest && r.latest.status === "done" && r.latest.errors === 0))) out.push("register");
  if (write && (r.status === "review" || r.status === "failed")) out.push("reject");
  // 등록된 작업도 재검수·원본 수정 가능 (다시 등록 = 연결된 원고의 새 버전). 원고와 연결돼 있으면 지울 수 없다
  if (refine && (r.canRerun ?? ["review", "registered", "rejected", "failed", "cancelled"].includes(r.status))) out.push("rerun");
  if (refine && !busy) out.push("source");
  if (refine && busy) out.push("cancel");
  if (refine && !busy && !r.manuscriptId) out.push("delete");
  return out;
}

function rowToJob(r: RefineRow, me: Me): RefineJob {
  return {
    id: r.id,
    title: r.title || "(제목 없음)",
    customerId: r.customerId,
    customer: r.customerName,
    status: r.status,
    author: r.createdByName ?? "—",
    createdAt: fmtDateTime(r.createdAt),
    folderId: r.folderId ?? "",
    sources: [{ revision: r.sourceRevision, body: "", files: [] }],
    attempts: [countedAttempt(r.latest)],
    manuscriptId: r.manuscriptId ?? undefined,
    allowedActions: allowedActionsOf(r, me),
  };
}

export const rowToJobList = (rows: RefineRow[], me: Me): RefineJob[] => rows.map((r) => rowToJob(r, me));

/** 상세 + 모든 시도 전체 + 모든 원본 버전 본문 → 시안 RefineJob. */
export function detailToJob(d: RefineDetailDto, attempts: AttemptDetail[], sources: SourceDto[], me: Me): RefineJob {
  const list = [...attempts].sort((a, b) => a.seq - b.seq).map(toAttempt);
  const srcs = [...sources].sort((a, b) => a.revision - b.revision).map((s) => ({ revision: s.revision, body: s.body, files: fileRefs(s.files) }));
  if (!srcs.some((s) => s.revision === d.sourceRevision)) srcs.push({ revision: d.sourceRevision, body: d.sourceBody, files: fileRefs(d.sourceFiles) });
  return {
    ...rowToJob(d, me),
    sources: srcs,
    attempts: list.length ? list : [countedAttempt(d.latest)],
    // 요청서 32절: 연결된 원고 제목·버전은 서버 조회값 그대로 (없으면 ID 만 보인다)
    linkedManuscript: d.manuscript ? { title: d.manuscript.title, revision: d.manuscript.revision } : undefined,
    registrationAttemptSeq: d.registrationAttemptSeq ?? undefined,
    allowedActions: allowedActionsOf(d, me),
  };
}

export function useRefineList(q: RefineListQuery, enabled = true) {
  return useQuery({
    queryKey: refineKeys.list(q),
    enabled,
    placeholderData: (prev) => prev,
    queryFn: () => api.page<RefineRow>(`/admin/manuscripts/refine${qs({ q: q.q.trim(), customerId: q.customerId, folderId: q.folderId, status: q.status, ids: q.ids, offset: q.offset, limit: q.limit })}`),
    // 큐 대기·정형화 중인 작업이 있으면 LIVE 간격으로 다시 읽는다
    refetchInterval: (query) => (query.state.data?.items.some((r) => r.status === "queued" || r.status === "running") ? LIVE() : false),
  });
}

/** 상세: 작업 + 시도 전체(이전 시도는 각각 조회) + 원본 버전 본문. 진행 중이면 LIVE 간격 폴링. */
export function useRefineDetail(id: string | null) {
  const detail = useQuery({
    queryKey: refineKeys.detail(id ?? ""),
    enabled: !!id,
    queryFn: () => api.get<RefineDetailDto>(`/admin/manuscripts/refine/${id}`),
    refetchInterval: (query) => (query.state.data && (query.state.data.status === "queued" || query.state.data.status === "running") ? LIVE() : false),
  });
  const d = detail.data;
  const latestSeq = d?.latestAttempt?.seq ?? null;
  const olderSeqs = (d?.attemptList ?? []).map((a) => a.seq).filter((s) => s !== latestSeq);
  const attempts = useQueries({
    queries: olderSeqs.map((seq) => ({
      queryKey: refineKeys.attempt(id ?? "", seq),
      enabled: !!id,
      staleTime: 60_000,
      queryFn: () => api.get<AttemptDetail>(`/admin/manuscripts/refine/${id}/attempts/${seq}`),
    })),
  });
  const olderRevisions = (d?.sources ?? []).map((s) => s.revision).filter((r) => r !== d?.sourceRevision);
  const sources = useQueries({
    queries: olderRevisions.map((revision) => ({
      queryKey: refineKeys.source(id ?? "", revision),
      enabled: !!id,
      staleTime: Infinity,
      queryFn: () => api.get<SourceDto>(`/admin/manuscripts/refine/${id}/sources/${revision}`),
    })),
  });
  const loading = detail.isLoading || attempts.some((a) => a.isLoading) || sources.some((s) => s.isLoading);
  const error = detail.error ?? attempts.find((a) => a.error)?.error ?? sources.find((s) => s.error)?.error ?? null;
  const build = (me: Me): RefineJob | undefined => {
    if (!d) return undefined;
    const all: AttemptDetail[] = [...attempts.map((a) => a.data).filter((a): a is AttemptDetail => !!a), ...(d.latestAttempt ? [d.latestAttempt] : [])];
    const srcs: SourceDto[] = [...sources.map((s) => s.data).filter((s): s is SourceDto => !!s), { revision: d.sourceRevision, body: d.sourceBody, files: d.sourceFiles, note: null, createdAt: d.updatedAt }];
    return detailToJob(d, all, srcs, me);
  };
  return { detail, loading, error: error ? errorMessage(error) : undefined, build, refetch: () => void detail.refetch() };
}

export function useRefineInvalidate() {
  const qc = useQueryClient();
  return async (id?: string) => {
    await qc.invalidateQueries({ queryKey: ["admin", "refine"] });
    if (id) await qc.invalidateQueries({ queryKey: refineKeys.detail(id) });
    await qc.invalidateQueries({ queryKey: ["admin", "manuscripts"] });
  };
}

/* ---------------- 가이드라인 ---------------- */

export function toGuideline(g: GuidelineRow): Guideline {
  return {
    id: g.id,
    kind: g.kind,
    version: g.version,
    label: g.label,
    body: g.body ?? "",
    active: g.status === "active",
    author: g.createdByName ?? "—",
    createdAt: fmtDateTime(g.createdAt),
    note: g.note ?? "",
    uses: g.attempts ?? 0,
  };
}

/** 버전 목록(본문 없음) + 버전마다 본문 (시안이 처음부터 본문·줄 비교를 그리므로 전부 읽는다. 버전 수는 적다). */
export function useGuidelines(enabled = true) {
  const list = useQuery({ queryKey: refineKeys.guidelines, enabled, queryFn: () => api.get<GuidelineRow[]>("/admin/manuscript-guidelines") });
  const bodies = useQueries({
    queries: (list.data ?? []).map((g) => ({
      queryKey: refineKeys.guideline(g.id),
      enabled,
      staleTime: Infinity,
      queryFn: () => api.get<GuidelineRow>(`/admin/manuscript-guidelines/${g.id}`),
    })),
  });
  const versions: Guideline[] = (list.data ?? []).map((g, i) => toGuideline({ ...g, body: bodies[i]?.data?.body ?? "" }));
  return { list, versions, loading: list.isLoading || bodies.some((b) => b.isLoading), error: list.error ?? bodies.find((b) => b.error)?.error ?? null };
}
